import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "./components/ui/sonner";

// Core components (keep synchronous as they are shared or critical)
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ScrollTopButton from "./components/ScrollTopButton";
import FloatingBar from "./components/FloatingBar/FloatingBar";
import SEO from './components/SEO';

import Hero from "./components/Hero";
// Lazy load all page components (Performance win: ~500KB+ reduction in initial JS)
// const Hero = lazy(() => import("./components/Hero"));
const About = lazy(() => import("./components/About"));
const Services = lazy(() => import("./components/Services"));
const CompanyHighlights = lazy(() => import("./components/CompanyHighlights"));
const Portfolio = lazy(() => import("./components/Portfolio"));
const Testimonials = lazy(() => import("./components/Testimonials"));
const Process = lazy(() => import("./components/Process"));
const Technologies = lazy(() => import("./components/Technologies"));
const FAQ = lazy(() => import("./components/FAQ"));
const Blog = lazy(() => import("./components/Blog"));
const Contact = lazy(() => import("./components/Contact"));
const DomainRegistration = lazy(() => import("./pages/DomainRegistration"));
const ProfessionalEmail = lazy(() => import("./components/ProfessionalEmail"));
const WebsiteDevelopment = lazy(() => import("./components/WebsiteDevelopment"));
const MobileAppDevelopment = lazy(() => import("./components/MobileAppDevelopment/MobileAppDevelopment"));
const Aboutuspage = lazy(() => import("./components/Aboutuspage/Aboutuspage"));
const Blogpage = lazy(() => import("./components/Blogpage/Blogpage"));
const Contactpage = lazy(() => import("./components/Contactpage/Contactpage"));
const PortfolioPage = lazy(() => import("./components/PortfolioPage/PortfolioPage"));
const WebHosting = lazy(() => import("./components/WebHosting"));
const DigitalMarketingPage = lazy(() => import("./components/DigitalMarketingPage/DigitalMarketingPage"));
const SocialMediaMarketingPage = lazy(() => import("./components/SocialMediaMarketingPage/SocialMediaMarketingPage"));
const SecurityPage = lazy(() => import("./components/SecurityPage/SecurityPage"));
const EcommercePage = lazy(() => import("./components/EcommercePage"));
const CloudStorageDatabasePage = lazy(() => import("./components/CloudStorageDatabasePage"));
const NotFound = lazy(() => import("./components/NotFound/NotFound"));

// Initial loading state
const PageLoader = () => (
  <div className="h-screen w-screen flex items-center justify-center bg-white">
    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "MBW Techimpex Pvt Ltd",
  "url": "https://mbwit.net/",
  "logo": "https://mbwit.net/logo.png",
  "description": "MBW Techimpex Pvt Ltd is a leading web design and digital marketing company in Chennai offering website development, SEO, Google Ads, Meta Ads, social media marketing, web hosting, domain registration, and mobile app development.",
  "telephone": "+91-9840886487",
  "email": "contact@mbwit.net",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "15 F, Valmiki Street, T. Nagar",
    "addressLocality": "Chennai",
    "addressRegion": "Tamil Nadu",
    "postalCode": "600017",
    "addressCountry": "IN"
  },
  "sameAs": [
    "https://www.linkedin.com/company/mbw-techimpex-p-ltd",
    "https://www.instagram.com/mbwtech",
    "https://www.facebook.com/MBWTechimpexPvtLtd"
  ]
};

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "About MBW Techimpex",
  "url": "https://mbwit.net/about",
  "description": "Learn about MBW Techimpex, a leading web design and digital marketing company in Chennai.",
  "mainEntity": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/",
    "logo": "https://mbwit.net/logo.png",
    "foundingLocation": {
      "@type": "Place",
      "name": "Chennai, Tamil Nadu, India"
    },
    "description": "MBW Techimpex provides website development, digital marketing, SEO, Google Ads, Meta Ads, web hosting, domain registration, and mobile app development."
  }
};

const domainSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Domain Registration Services",
  "serviceType": "Domain Name Registration",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides domain registration, domain transfer, renewal, and domain management services for businesses and individuals.",
  "url": "https://mbwit.net/domain-registration"
};

const webDevSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Website Development Services",
  "serviceType": "Website Development",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides professional website development services including business websites, responsive web design, e-commerce websites, and custom web applications.",
  "url": "https://mbwit.net/website-development"
};

const emailSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Professional Business Email Services",
  "serviceType": "Business Email Hosting",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex offers professional business email hosting, custom domain email addresses, secure email services, spam protection, and business communication solutions.",
  "url": "https://mbwit.net/professional-email"
};

const mobileAppSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Mobile App Development Services",
  "serviceType": "Mobile Application Development",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides Android app development, iOS app development, hybrid mobile app development, enterprise mobile applications, and custom mobile app solutions.",
  "url": "https://mbwit.net/mobile-app-development"
};

const hostingSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Web Hosting Services",
  "serviceType": "Website Hosting",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex offers secure and reliable web hosting services including Linux hosting, Windows hosting, SSD hosting, business email hosting, SSL certificates, and technical support.",
  "url": "https://mbwit.net/web-hosting"
};

const blogSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "MBW Techimpex Blog",
  "url": "https://mbwit.net/blog",
  "description": "The official MBW Techimpex blog featuring articles on digital marketing, website development, SEO, Google Ads, Meta Ads, social media marketing, and technology.",
  "publisher": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  }
};

const contactSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "MBW Techimpex Pvt Ltd",
  "url": "https://mbwit.net/",
  "image": "https://mbwit.net/logo.png",
  "telephone": "+91-44-42605227",
  "email": "contact@mbwit.net",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "15 F, Valmiki Street",
    "addressLocality": "Chennai",
    "addressRegion": "Tamil Nadu",
    "postalCode": "600017",
    "addressCountry": "IN"
  },
  "openingHours": "Mo-Sa 09:30-18:30",
  "areaServed": "Chennai",
  "sameAs": [
    "https://www.facebook.com/MBWTechimpexPvtLtd",
    "https://www.instagram.com/mbwtech",
    "https://www.linkedin.com/company/mbw-techimpex-p-ltd"
  ]
};

const portfolioSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "MBW Techimpex Portfolio",
  "url": "https://mbwit.net/portfolio",
  "description": "Portfolio showcasing website development, digital marketing, SEO, e-commerce, and mobile application projects completed by MBW Techimpex.",
  "publisher": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  }
};

const marketingSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Digital Marketing Services",
  "serviceType": "Digital Marketing",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides SEO, Google Ads, Meta Ads, social media marketing, lead generation, and performance marketing services to help businesses grow online.",
  "url": "https://mbwit.net/digital-marketing"
};

const socialMediaSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Social Media Marketing Services",
  "serviceType": "Social Media Marketing",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides professional social media marketing services including Facebook marketing, Instagram marketing, LinkedIn marketing, Meta Ads management, content creation, and brand promotion.",
  "url": "https://mbwit.net/social-media"
};

const securitySchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Website & Domain Security Services",
  "serviceType": "Website Security",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net/"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides website security services including SSL certificates, malware removal, website firewall protection, vulnerability scanning, website backup, and domain security.",
  "url": "https://mbwit.net/security"
};

const ecommerceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "E-Commerce Website Development",
  "serviceType": "E-Commerce Website Development",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides professional e-commerce website development services in Chennai, including custom online stores, shopping cart development, secure payment gateway integration, mobile-friendly design, and SEO-ready solutions.",
  "url": "https://mbwit.net/ecommerce-website-development",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock"
  }
};

const cloudStorageSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Enterprise Cloud Storage & Database Solutions",
  "serviceType": "Enterprise Cloud Storage & Database Solutions",
  "provider": {
    "@type": "Organization",
    "name": "MBW Techimpex Pvt Ltd",
    "url": "https://mbwit.net"
  },
  "areaServed": {
    "@type": "City",
    "name": "Chennai"
  },
  "description": "MBW Techimpex provides Enterprise Cloud Storage & Database Solutions, including secure cloud storage, database management, cloud migration, backup, disaster recovery, and scalable business cloud infrastructure.",
  "url": "https://mbwit.net/enterprise-cloud-storage-database-solutions",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock"
  }
};

const Home = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Web Design & Digital Marketing Company in Chennai | MBW Techimpex"
        description="MBW Techimpex is a leading web design and digital marketing company in Chennai offering website development, SEO, Google Ads, Meta Ads, hosting, and mobile app development."
        keywords="Web Design Company in Chennai, Website Development Company Chennai,
                    Digital Marketing Company Chennai,
                    SEO Company Chennai,
                    Google Ads Agency T.nagar Chennai,
                    Meta Ads Agency T.nagar Chennai,
                    E-Commerce Web Design Company T.nagar Chennai,
                    E-Commerce Website Development Company in Chennai,
                    Enterprise Cloud Storage Chennai,
                    Cloud Storage Solutions Chennai"
        schema={homeSchema}
      />
      <Header />
      <main>
        <Hero />
        <About />
        <Services />
        <CompanyHighlights />
        <Portfolio />
        <Testimonials />
        <Process />
        <Technologies />
        <FAQ />
        <Blog />
        <Contact />
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop /> {/* ✅ This triggers scroll on route changes */}
          <FloatingBar />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />

              <Route path="/domain-registration" element={
                <>
                  <SEO
                    title="Domain Registration Services in Chennai "
                    description="Register your domain name with MBW Techimpex in Chennai. We offer .com, .in, .org, .net, and other domain extensions with secure management and expert support."
                    keywords="Domain Registration Chennai,
                                Domain Name Registration,
                                Buy Domain Name India,
                                Domain Registration Company Chennai,
                                Website Domain Registration
                            "
                    schema={domainSchema}
                  />
                  <Header />
                  <DomainRegistration />
                  <Footer />
                  <Toaster />
                </>
              } />

              <Route path="/website-development" element={
                <>
                  <SEO
                    title="Website Development Company in Chennai "
                    description="MBW Techimpex is a trusted website development company in Chennai offering responsive business websites, e-commerce solutions, custom web applications, and SEO-friendly web development."
                    keywords="Web Development Company Chennai,
                                Website Design Company Chennai,
                                Custom Website Development,
                                Business Website Development,
                                Corporate Website Development,
                                E-Commerce Website Development"
                    schema={webDevSchema}
                  />
                  <Header />
                  <WebsiteDevelopment />
                  <Footer />
                  <Toaster />
                </>
              } />

              <Route path="/professional-email" element={
                <>
                  <SEO
                    title="Professional Business Email Services in Chennai"
                    description="Get secure professional business email services in Chennai with your own domain name. MBW Techimpex provides reliable email hosting, spam protection, and business email solutions."
                    keywords="Business Email Hosting Chennai,
                                Professional Email Services,
                                Corporate Email Solutions,
                                Domain Email Hosting,
                                Business Email Setup,
                                Secure Email Hosting"
                    schema={emailSchema}
                  />
                  <Header />
                  <ProfessionalEmail />
                  <Footer />
                  <Toaster />
                </>
              } />

              <Route path="/mobile-app-development" element={
                <>
                  <SEO
                    title="Mobile App Development Company in Chennai"
                    description="MBW Techimpex is a trusted mobile app development company in Chennai offering Android app development, iOS app development, hybrid apps, and custom mobile application solutions."
                    keywords="Mobile App Development Chennai,
                                Android App Development Company,
                                iOS App Development Company,
                                Hybrid App Development,
                                Custom Mobile App Development,
                                Business Mobile Applications"
                    schema={mobileAppSchema}
                  />
                  <MobileAppDevelopment />
                </>
              } />

              <Route path="/web-hosting" element={
                <>
                  <SEO
                    title="Web Hosting Services in Chennai | Fast & Secure Hosting"
                    description="Get fast, secure, and reliable web hosting services in Chennai with 99.9% uptime, SSD hosting, SSL support, email hosting, and expert technical support from MBW Techimpex."
                    keywords="Web Hosting Services Chennai,
                              Web Hosting Company Chennai,
                              Linux Web Hosting,
                              Windows Web Hosting,
                              Business Web Hosting,"
                    schema={hostingSchema}
                  />
                  <Header />
                  <WebHosting />
                  <Footer />
                  <Toaster />
                </>
              } />

              <Route path="/about" element={
                <>
                  <SEO
                    title="About MBW Techimpex | Web Design & Digital Marketing Company Chennai"
                    description="Learn about MBW Techimpex, a trusted web design and digital marketing company in Chennai delivering website development, SEO, Google Ads, Meta Ads, hosting, and mobile app solutions."
                    keywords="Web Design Company in Chennai, Website Development Company Chennai,
                                Digital Marketing Company Chennai,
                              SEO Company Chennai,
                              Google Ads Agency Chennai,
                              Meta Ads Agency Chennai"
                    schema={aboutSchema}
                  />
                  <Aboutuspage />
                </>
              } />

              <Route path="/blog" element={
                <>
                  <SEO
                    title="Digital Marketing & Web Development Blog"
                    description="Read the latest blogs from MBW Techimpex on digital marketing, website development, SEO, Google Ads, social media marketing, technology trends, and business growth strategies."
                    keywords="Web Development Blog,
                              SEO Tips,
                              Digital Marketing Articles,
                              Google Ads Guide,
                              Social Media Marketing Blog,
                              Website Development Tips"
                    schema={blogSchema}
                  />
                  <Blogpage />
                </>
              } />

              <Route path="/contact" element={
                <>
                  <SEO
                    title="Contact MBW Techimpex | Web Design Company in Chennai"
                    description=" Contact MBW Techimpex in Chennai for website development, digital marketing, SEO, Google Ads, Meta Ads, web hosting, domain registration, and mobile app development services."
                    keywords="Contact Digital Marketing Company Chennai,
                              Website Development Company Chennai,
                              SEO Company Chennai,
                              Google Ads Agency Chennai,
                              Meta Ads Agency Chennai,
                              Mobile App Development Chennai,
                              Web Hosting Company Chennai,
                              Domain Registration Chennai,
                              Contact MBW Techimpex,
                              Business Website Solutions Chennai"
                    schema={contactSchema}
                  />
                  <Contactpage />
                </>
              } />

              <Route path="/portfolio" element={
                <>
                  <SEO
                    title="Our Portfolio | Website Design & Digital Marketing Projects"
                    description="Explore MBW Techimpex's portfolio of successful website development, digital marketing, SEO, e-commerce, and mobile app projects delivered for businesses across various industries"
                    keywords="Web Design Portfolio,
                          Website Development Projects,
                          Digital Marketing Portfolio,
                          SEO Success Stories,
                          E-commerce Website Portfolio,
                          Mobile App Development Portfolio,
                          Web Design Company Chennai,
                          Website Design Case Studies,
                          Client Projects,
                          MBW Techimpex Portfolio"
                    schema={portfolioSchema}
                  />
                  <PortfolioPage />
                </>
              } />

              <Route path="/digital-marketing" element={
                <>
                  <SEO
                    title="Digital Marketing Company in Chennai | SEO, Google Ads"
                    description=" Grow your business with MBW Techimpex, a leading digital marketing company in Chennai offering SEO, Google Ads, Meta Ads, social media marketing, and lead generation services."
                    keywords="Web Development Blog,
                              SEO Tips,
                              Digital Marketing Articles,
                              Google Ads Guide,
                              Social Media Marketing Blog,
                              Website Development Tips"
                    schema={marketingSchema}
                  />
                  <DigitalMarketingPage />
                </>
              } />

              <Route path="/social-media" element={
                <>
                  <SEO
                    title="Social Media Marketing Company in Chennai"
                    description="Boost your brand with MBW Techimpex, a leading social media marketing company in Chennai offering Facebook, Instagram, LinkedIn marketing, content creation, Meta Ads, and lead generation services."
                    keywords="Social Media Marketing Services Chennai,
Social Media Agency Chennai,
Facebook Marketing Services,
Instagram Marketing Company,
LinkedIn Marketing Services,
Meta Ads Agency Chennai"
                    schema={socialMediaSchema}
                  />
                  <SocialMediaMarketingPage />
                </>
              } />

              <Route path="/security" element={
                <>
                  <SEO
                    title="Website & Domain Security Services in Chennai "
                    description="Protect your website and domain with MBW Techimpex. We provide website security, SSL certificates, malware removal, vulnerability scanning, firewall protection, and domain security services in Chennai."
                    keywords="Website & Domain Security,
Website Security Company Chennai,
Domain Security Services,
SSL Certificate Services,
Website Malware Removal,
Website Firewall Protection"
                    schema={securitySchema}
                  />
                  <SecurityPage />
                </>
              } />

              <Route path="/e-commerce" element={
                <>
                  <SEO
                    title="E-Commerce Web Design Company in Chennai | MBW Techimpex"
                    description="Grow your business with professional e-commerce website development in Chennai. MBW Techimpex builds secure, mobile-friendly online stores with payment gateway integration, SEO, and complete support."
                    keywords="E-Commerce Web Design Company in T-Nagar,
                            E-Commerce Website Development Company in Chennai,
                            E-Commerce Website Development,
                            E-Commerce Website,
                            Online Store Development,
                            E-Commerce Website Design,
                            E-Commerce Website Company,
                            E-Commerce Website Developers,
                            E-Commerce Website Development Chennai,
                            E-Commerce Web Design Company Chennai"
                    schema={ecommerceSchema}
                  />
                  <Header />
                  <EcommercePage />
                  <Footer />
                  <Toaster />
                </>
              } />

              <Route path="/cloud-storage-database" element={
                <>
                  <SEO
                    title="Enterprise Cloud Storage & Database Solutions Chennai | MBW"
                    description="Secure your business with Enterprise Cloud Storage & Database Solutions. MBW Techimpex offers scalable cloud storage, database management, backup, disaster recovery, and secure cloud solutions."
                    keywords="Enterprise Cloud Storage,
                            Cloud Storage Solutions,
                            Database Solutions,
                            Enterprise Database Management,
                            Enterprise Cloud Storage Chennai,
                            Cloud Storage Solutions Chennai,
                            Database Management Company Chennai,
                            Cloud Database Solutions Chennai"
                    schema={cloudStorageSchema}
                  />
                  <Header />
                  <CloudStorageDatabasePage />
                  <Footer />
                  <Toaster />
                </>
              } />

              {/* 404 Route */}
              <Route path="*" element={
                <>
                  <SEO
                    title="Page Not Found"
                    description="The page you are looking for does not exist."
                  />
                  <NotFound />
                </>
              } />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
